import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import type {
  PracticeLabSessionResponse,
  PracticeLabStepStateResponse,
} from "@/types/practice-lab";

// The lab store is deliberately tiny. The backend computes ALL derived state
// (rail_status, outcome, reviews_left, verified_count, session status) and every
// SSE event is a full snapshot, so the store just holds the latest snapshot and
// replaces it wholesale. No reducers, no per-step drafts — unlike the assessment
// store, the projection lives in the render, not here.

export type LabConnection = "connecting" | "open" | "error";

interface PracticeLabState {
  snapshot: PracticeLabSessionResponse | null;
  connection: LabConnection;
  setSnapshot: (snapshot: PracticeLabSessionResponse) => void;
  setConnection: (connection: LabConnection) => void;
  reset: () => void;
}

const initialState = {
  snapshot: null as PracticeLabSessionResponse | null,
  connection: "connecting" as LabConnection,
};

export const usePracticeLabStore = create<PracticeLabState>((set) => ({
  ...initialState,
  setSnapshot: (snapshot) => set({ snapshot }),
  setConnection: (connection) => set({ connection }),
  reset: () => set({ ...initialState }),
}));

// ---- Selectors ----

export const useLabSnapshot = () => usePracticeLabStore((s) => s.snapshot);

export const useLabConnection = () => usePracticeLabStore((s) => s.connection);

/** The step the member is currently on — the `active` rail step, or the one
 *  matching current_step. Returns a stable reference until the snapshot changes. */
export const useActiveStepState = (): PracticeLabStepStateResponse | null =>
  usePracticeLabStore((s) => {
    const snap = s.snapshot;
    if (!snap) return null;
    return (
      snap.steps.find((st) => st.rail_status === "active") ??
      snap.steps.find((st) => st.order === snap.current_step) ??
      null
    );
  });

export const useVerifiedProgress = () =>
  usePracticeLabStore(
    useShallow((s) => {
      const total = s.snapshot?.step_count ?? 0;
      const verified = s.snapshot?.verified_count ?? 0;
      return {
        verified,
        total,
        percentage: total > 0 ? (verified / total) * 100 : 0,
      };
    })
  );
